import { useState, useEffect, useCallback, useRef, useId, type FormEvent } from 'react';
import Stepper from './Stepper';
import { readJoinPreview, resolveZoomConfig } from '../lib/zoomConfig';
import { ensureZoomSdkScript, isZoomSdkReady } from '../lib/zoomSdk';
import { traceJoin } from '../lib/joinTrace';
import { stepIndex, type Step } from '../lib/steps';
import { todayAtTime, formatDateTime, formatDateOnly } from '../lib/datetime';
import { getTimeWindowStatus } from '../lib/timeWindow';
import {
  DEPARTMENTS,
  validateForm,
  type AppointmentData,
  type FormErrors,
} from '../lib/validation';
import { parseParams } from '../lib/searchParams';
import { getAudioContextCtor, playTestTone } from '../lib/audio';
import { withRetry } from '../lib/withRetry';

type CheckStatus = 'pending' | 'checking' | 'granted' | 'denied';

function setGlobalVars(data: AppointmentData) {
  const w = window as unknown as Record<string, unknown>;
  w.appointmentFirstName = data.firstName;
  w.appointmentLastName = data.lastName;
  w.appointmentDepartment = data.department;
  w.appointmentDateTime = todayAtTime(data.time);
}

// ── Card chrome ─────────────────────────────────────────────────

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-white border border-card-border rounded-lg shadow-[0_1px_2px_rgba(33,43,50,0.04)] p-6 lg:p-9 mt-5">
      {children}
    </div>
  );
}

function ScreenHeading({ title, intro, hint }: { title: string; intro: string; hint?: string }) {
  return (
    <>
      <h2 className="text-[1.5rem] lg:text-[1.625rem] font-bold text-text mb-2 leading-snug">
        {title}
      </h2>
      <p className="leading-relaxed text-text mb-1">{intro}</p>
      {hint && <p className="text-text-muted text-[15px] mb-0">{hint}</p>}
    </>
  );
}

// ── Status icons ────────────────────────────────────────────────

function StatusIcon({ status }: { status: CheckStatus }) {
  if (status === 'pending')
    return (
      <span
        className="inline-flex items-center justify-center w-7 h-7 rounded-full border-2 border-border-input"
        aria-hidden="true"
      />
    );
  if (status === 'checking')
    return (
      <span
        className="inline-flex items-center justify-center w-7 h-7 rounded-full border-2 border-nhs-blue text-nhs-blue motion-safe:animate-spin"
        aria-hidden="true"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <path d="M12 2a10 10 0 0 1 10 10" />
        </svg>
      </span>
    );
  if (status === 'granted')
    return (
      <span
        className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-nhs-green text-white"
        aria-hidden="true"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M5 13l4 4L19 7" />
        </svg>
      </span>
    );
  return (
    <span
      className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-nhs-red text-white"
      aria-hidden="true"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M18 6L6 18M6 6l12 12" />
      </svg>
    </span>
  );
}

function statusLabel(status: CheckStatus) {
  switch (status) {
    case 'pending':
      return 'Not checked';
    case 'checking':
      return 'Checking…';
    case 'granted':
      return 'Ready';
    case 'denied':
      return 'Access denied';
  }
}

// ── Confirm view ────────────────────────────────────────────────

function ConfirmView({
  data,
  onBack,
  onConfirm,
}: {
  data: AppointmentData;
  onBack: () => void;
  onConfirm: () => void;
}) {
  const appointmentIso = data.time ? todayAtTime(data.time) : '';

  return (
    <Card>
      <ScreenHeading
        title="Confirm your details"
        intro="Please check the details below are correct before proceeding."
      />

      <dl className="mt-6 mb-8 grid sm:grid-cols-[180px_1fr] gap-y-4 sm:gap-x-6">
        <dt className="font-bold text-text">Patient first name</dt>
        <dd className="m-0 text-text">{data.firstName}</dd>
        <dt className="font-bold text-text">Patient last name</dt>
        <dd className="m-0 text-text">{data.lastName}</dd>
        <dt className="font-bold text-text">Department</dt>
        <dd className="m-0 text-text">{data.department}</dd>
        <dt className="font-bold text-text">Date &amp; time of appointment</dt>
        <dd className="m-0 text-text">{formatDateTime(appointmentIso)}</dd>
      </dl>

      <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
        <button
          type="button"
          onClick={onBack}
          className="border-2 border-nhs-blue text-nhs-blue font-bold px-6 py-3 text-base hover:bg-nhs-blue hover:text-white rounded-sm transition-colors min-h-12"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="bg-nhs-blue text-white font-bold px-6 py-3 text-base hover:bg-nhs-dark-blue rounded-sm transition-colors min-h-12"
        >
          Confirm details
        </button>
      </div>
    </Card>
  );
}

// ── AV check view ───────────────────────────────────────────────

type VideoEndReason = 'user' | 'dropped' | null;
type JoinPhase = 'idle' | 'connecting' | 'still-waiting';

/** Delay before the still-waiting copy appears; long enough that a normal join never shows it. */
export const JOIN_STILL_WAITING_MS = 15_000;

const JOIN_CONNECTING_MESSAGE = 'Connecting to your appointment. Please wait.';
const JOIN_STILL_WAITING_MESSAGE =
  'Still connecting — this can take a minute. Please wait and do not refresh.';
const JOIN_ERROR_MESSAGE = 'We could not start your appointment. Please try again.';

/**
 * Whether the Zoom widget is on the page is what drives the join UI, because
 * the SDK's `video-start` / `video-end` events do not fire reliably — a call
 * can be up, or long since over, while we are still waiting on one.
 *
 * The SDK appends this container to `<body>` when the call UI opens and removes
 * it when the patient leaves. Its sibling `#__zcc_video_modal_root__` outlives
 * the call, so it cannot stand in for this one.
 */
const ZOOM_WIDGET_SELECTOR = '#__zoom_video_client__';

/** The widget can render well after `startVideo()` resolves, so re-check rather than probe once. */
const ZOOM_WIDGET_POLL_MS = 500;

function zoomWidgetPresent(): boolean {
  return document.querySelector(ZOOM_WIDGET_SELECTOR) !== null;
}

function joinStatusText(phase: JoinPhase): string {
  if (phase === 'connecting') return JOIN_CONNECTING_MESSAGE;
  if (phase === 'still-waiting') return JOIN_STILL_WAITING_MESSAGE;
  return '';
}

function AVCheckView({
  patientName,
  appointmentDateTime,
  onReset,
  onAllReady,
}: {
  patientName: string;
  appointmentDateTime: string;
  onReset: () => void;
  onAllReady?: () => void;
}) {
  const [camera, setCamera] = useState<CheckStatus>('pending');
  const [microphone, setMicrophone] = useState<CheckStatus>('pending');
  const [speaker, setSpeaker] = useState<CheckStatus>('pending');
  const [started, setStarted] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [timeStatus, setTimeStatus] = useState(() => getTimeWindowStatus(appointmentDateTime));
  const [videoEnded, setVideoEnded] = useState<VideoEndReason>(null);
  const endReasonRef = useRef<VideoEndReason>(null);
  const endHandledRef = useRef(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const videoClientRef = useRef<any>(null);

  const [speakerTested, setSpeakerTested] = useState(false);
  const [speakerPlaying, setSpeakerPlaying] = useState(false);
  const [joinPhase, setJoinPhase] = useState<JoinPhase>('idle');
  const [joinError, setJoinError] = useState(false);
  const joinInFlightRef = useRef(false);
  const stillWaitingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const joinErrorRef = useRef<HTMLDivElement>(null);
  const stopWidgetWatchRef = useRef<(() => void) | null>(null);
  const joining = joinPhase !== 'idle';

  function clearJoinWaitTimer() {
    if (stillWaitingTimerRef.current) {
      clearTimeout(stillWaitingTimerRef.current);
      stillWaitingTimerRef.current = null;
    }
  }

  function finishConnectingUi() {
    joinInFlightRef.current = false;
    clearJoinWaitTimer();
    setJoinPhase('idle');
  }

  function stopWidgetWatch() {
    stopWidgetWatchRef.current?.();
    stopWidgetWatchRef.current = null;
  }

  /**
   * End the call once, on whichever signal arrives first. Zoom emits
   * `video-force-end` about a second after a normal `video-end`, so anything
   * arriving after the first signal must not be able to reclassify the call.
   */
  function endCall(reason: Exclude<VideoEndReason, null>) {
    if (endHandledRef.current) return;
    endHandledRef.current = true;
    traceJoin(`ending as "${reason}"`);
    stopWidgetWatch();
    finishConnectingUi();
    setVideoEnded(reason);
  }

  /**
   * Watch the Zoom widget in and out of the DOM: its first appearance means the
   * patient is in the call, and its removal means they have left.
   */
  function watchZoomWidget() {
    stopWidgetWatch();
    let seen = false;

    const check = () => {
      const present = zoomWidgetPresent();
      if (present) {
        if (!seen) {
          seen = true;
          traceJoin('widget appeared');
          finishConnectingUi();
        }
        return;
      }
      if (seen) {
        traceJoin('widget removed');
        endCall(endReasonRef.current || 'user');
      }
    };

    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true });
    const poll = setInterval(check, ZOOM_WIDGET_POLL_MS);
    stopWidgetWatchRef.current = () => {
      observer.disconnect();
      clearInterval(poll);
    };
    check();
  }

  function beginConnecting() {
    setJoinError(false);
    setJoinPhase('connecting');
    clearJoinWaitTimer();
    stillWaitingTimerRef.current = setTimeout(() => {
      setJoinPhase(prev => (prev === 'connecting' ? 'still-waiting' : prev));
    }, JOIN_STILL_WAITING_MS);
  }

  useEffect(() => {
    return () => {
      if (stillWaitingTimerRef.current) {
        clearTimeout(stillWaitingTimerRef.current);
      }
      stopWidgetWatchRef.current?.();
      stopWidgetWatchRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (joinError) {
      joinErrorRef.current?.focus();
    }
  }, [joinError]);

  async function handleTestTone() {
    if (speakerPlaying) return;
    setSpeakerPlaying(true);
    try {
      await playTestTone();
    } catch {
      // silently ignore — user will notice they can't hear it
    } finally {
      setSpeakerPlaying(false);
      setSpeakerTested(true);
    }
  }

  const allReady = camera === 'granted' && microphone === 'granted' && speaker === 'granted';
  const anyDenied = camera === 'denied' || microphone === 'denied' || speaker === 'denied';

  useEffect(() => {
    if (allReady && sdkReady) {
      onAllReady?.();
    }
  }, [allReady, sdkReady, onAllReady]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeStatus(getTimeWindowStatus(appointmentDateTime));
    }, 30000);
    return () => clearInterval(interval);
  }, [appointmentDateTime]);

  const runChecks = useCallback(async () => {
    if (started) return;
    setStarted(true);

    if (!navigator.mediaDevices?.getUserMedia) {
      setCamera('denied');
      setMicrophone('denied');
      setSpeaker('denied');
      return;
    }

    setCamera('checking');
    try {
      await withRetry(
        async () => {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true });
          stream.getTracks().forEach(t => t.stop());
        },
        3,
        600
      );
      setCamera('granted');
    } catch {
      setCamera('denied');
    }

    await new Promise(r => setTimeout(r, 500));

    setMicrophone('checking');
    try {
      await withRetry(
        async () => {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach(t => t.stop());
        },
        3,
        600
      );
      setMicrophone('granted');
    } catch {
      setMicrophone('denied');
    }

    // Speaker check is intentionally a feature-detect only.
    //
    // We do NOT call `ctx.resume()` here because by this point the page's
    // transient user activation has been consumed by the camera and mic
    // permission prompts (those clicks go to the browser permission UI,
    // not the page, so they don't refresh activation). On stock desktop
    // Chrome and on iOS, `resume()` would then sit pending forever
    // waiting for a fresh gesture, the timeout would fire, and the user
    // sees a confusing "Access denied" against speakers.
    //
    // Audibility cannot be verified from JS anyway — the user verifies
    // it themselves with the "Play test tone" button below, which IS
    // invoked from a real click and so can resume the AudioContext.
    setSpeaker('checking');
    try {
      const ACtor = getAudioContextCtor();
      if (!ACtor) {
        throw new Error('AudioContext not supported');
      }
      const probe = new ACtor();
      probe.close().catch(() => {});
      setSpeaker('granted');
    } catch {
      setSpeaker('denied');
    }
  }, [started]);

  useEffect(() => {
    runChecks();
  }, [runChecks]);

  useEffect(() => {
    const script = ensureZoomSdkScript();
    if (isZoomSdkReady()) {
      setSdkReady(true);
      return;
    }
    const onLoad = () => setSdkReady(true);
    script.addEventListener('load', onLoad);
    return () => script.removeEventListener('load', onLoad);
  }, []);

  async function handleStartAppointment() {
    if (joinInFlightRef.current) return;
    joinInFlightRef.current = true;

    setVideoEnded(null);
    endReasonRef.current = null;
    endHandledRef.current = false;
    beginConnecting();
    traceJoin('Start clicked');

    const preview = readJoinPreview();
    if (preview) {
      traceJoin(`join preview "${preview}" — not starting Zoom`);
      if (preview === 'error') {
        finishConnectingUi();
        setJoinError(true);
      }
      return;
    }

    const win = window as unknown as Record<string, unknown>;
    const VClient = win.VideoClient as {
      new (opts: object): {
        init(opts: { entryId: string; name?: string }): Promise<void>;
        startVideo(): void;
        on(event: string, handler: () => void): void;
      };
    };
    if (!VClient) {
      traceJoin('SDK not loaded');
      finishConnectingUi();
      setJoinError(true);
      return;
    }
    const client = new VClient({});
    videoClientRef.current = client;

    function registerEvents() {
      const events = [
        'video-start',
        'video-end',
        'video-click-end',
        'video-force-end',
        'notification-join-call',
        'task-created',
      ];
      events.forEach(evt => {
        client.on(evt, () => {
          traceJoin(`sdk event "${evt}"`);
          if (evt === 'video-start') finishConnectingUi();
          if (evt === 'video-click-end') endReasonRef.current = 'user';
          // Only a force-end that arrives before the call has ended means a
          // genuine drop; the routine one that trails `video-end` is ignored
          // by `endCall`.
          if (evt === 'video-force-end') endReasonRef.current = 'dropped';
          if (evt === 'video-end') endCall(endReasonRef.current || 'user');
        });
      });
    }

    registerEvents();
    try {
      await client.init({
        entryId: resolveZoomConfig().entryId,
        name: patientName,
      });
      client.startVideo();
    } catch {
      traceJoin('init failed');
      finishConnectingUi();
      setJoinError(true);
      return;
    }
    registerEvents();
    watchZoomWidget();
  }

  const joinButtonClassName =
    'bg-nhs-green text-white font-bold px-6 py-3 text-base rounded-sm min-h-12' +
    (joining ? ' cursor-not-allowed' : ' hover:opacity-90 transition-opacity');

  // After a normal end the appointment is over, so rejoining is a way back from
  // a mistake rather than the expected next step — it should not compete with
  // the "what happens next" copy the way a primary button would.
  const rejoinButtonClassName =
    'border-2 border-nhs-blue text-nhs-blue font-bold px-6 py-3 text-base rounded-sm min-h-12' +
    (joining ? ' cursor-not-allowed' : ' hover:bg-nhs-blue hover:text-white transition-colors');

  if (videoEnded) {
    const wasDropped = videoEnded === 'dropped';
    return (
      <Card>
        <ScreenHeading
          title={wasDropped ? 'Connection lost' : 'Thank you'}
          intro={
            wasDropped
              ? 'It looks like your video appointment was disconnected unexpectedly. You can rejoin below if your appointment is still in progress.'
              : 'Your virtual appointment has ended. Thank you for using our virtual hospital service.'
          }
        />
        <div className="space-y-4 mt-6 mb-6">
          {!wasDropped && (
            <>
              <p className="text-text">
                <strong>What happens next?</strong> Your clinician will update your records and
                follow up with any next steps. If you have any concerns, please contact your
                department directly.
              </p>
              <p className="text-text">
                If you need further assistance, call us on <strong>01737 768 511</strong> or visit{' '}
                <a href="#" className="text-nhs-blue underline">
                  our contact page
                </a>
                .
              </p>
            </>
          )}
          {wasDropped && (
            <p className="text-text">
              If the issue persists, please check your internet connection and try again. You can
              also call us on <strong>01737 768 511</strong> for assistance.
            </p>
          )}
        </div>

        {timeStatus.allowed && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            {!wasDropped && (
              <p className="text-text m-0">
                Ended by mistake? You can rejoin while your appointment slot is open.
              </p>
            )}
            <button
              type="button"
              aria-disabled={joining || undefined}
              aria-busy={joining || undefined}
              onClick={handleStartAppointment}
              className={`sm:ml-auto ${wasDropped ? joinButtonClassName : rejoinButtonClassName}`}
            >
              {joining ? 'Connecting…' : wasDropped ? 'Rejoin appointment' : 'Rejoin'}
            </button>
          </div>
        )}
      </Card>
    );
  }

  return (
    <Card>
      <ScreenHeading
        title="Equipment check"
        intro="We need to verify your device has access to a camera, microphone and speakers for your virtual appointment."
      />

      <ul className="list-none p-0 mt-6 mb-8 space-y-5" aria-label="Device checks">
        {[
          { label: 'Camera', status: camera },
          { label: 'Microphone', status: microphone },
        ].map(({ label, status }) => (
          <li key={label} className="flex items-center gap-4">
            <StatusIcon status={status} />
            <div>
              <p className="font-bold text-text m-0">{label}</p>
              <p className="text-sm text-text-muted m-0" aria-live="polite" aria-atomic="true">
                <span className="sr-only">{label} status: </span>
                {statusLabel(status)}
              </p>
            </div>
          </li>
        ))}

        <li className="flex items-start gap-4">
          <span className="mt-1">
            <StatusIcon status={speaker} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-text m-0">Speakers</p>
            <p className="text-sm text-text-muted m-0" aria-live="polite" aria-atomic="true">
              <span className="sr-only">Speakers status: </span>
              {statusLabel(speaker)}
            </p>
            {speaker === 'granted' && (
              <div className="mt-3 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={handleTestTone}
                  disabled={speakerPlaying}
                  aria-label={
                    speakerPlaying
                      ? 'Playing test tone…'
                      : speakerTested
                        ? 'Play test tone again'
                        : 'Play a test tone to check your speakers'
                  }
                  className={[
                    'self-start inline-flex items-center gap-2 border-2 border-nhs-blue text-nhs-blue font-bold',
                    'px-4 py-2 text-sm rounded-sm transition-colors min-h-11 min-w-11',
                    speakerPlaying
                      ? 'opacity-60 cursor-not-allowed'
                      : 'hover:bg-nhs-blue hover:text-white',
                  ].join(' ')}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    {speakerPlaying ? (
                      <>
                        <line x1="1" y1="10" x2="1" y2="14" />
                        <line x1="5" y1="6" x2="5" y2="18" />
                        <line x1="9" y1="8" x2="9" y2="16" />
                        <line x1="13" y1="4" x2="13" y2="20" />
                        <line x1="17" y1="8" x2="17" y2="16" />
                        <line x1="21" y1="10" x2="21" y2="14" />
                      </>
                    ) : (
                      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    )}
                  </svg>
                  {speakerPlaying ? 'Playing…' : speakerTested ? 'Play again' : 'Play test tone'}
                </button>
                {/*
                  Persistent live region — must stay in the DOM so the screen
                  reader registers it before content changes. Text is swapped
                  in-place rather than conditionally mounting two elements.
                */}
                <p className="text-sm text-text-muted m-0" aria-live="polite" aria-atomic="true">
                  {speakerPlaying
                    ? 'Playing\u2026'
                    : speakerTested
                      ? `If you couldn't hear it, check your ${window.matchMedia('(pointer: coarse)').matches ? 'silent mode switch, ' : ''}volume or headphones and play again.`
                      : 'Test your speakers before joining.'}
                </p>
              </div>
            )}
          </div>
        </li>
      </ul>

      {allReady && sdkReady && timeStatus.allowed && (
        <div className="flex flex-col gap-4">
          {joinError && (
            <div
              ref={joinErrorRef}
              role="alert"
              tabIndex={-1}
              className="flex flex-col gap-2 p-4 border-l-4 border-l-nhs-red bg-callout-bg rounded-sm outline-none"
            >
              <p className="text-text m-0">{JOIN_ERROR_MESSAGE}</p>
              <p className="text-text m-0 text-[15px]">
                If the issue persists, call us on <strong>01737 768 511</strong>.
              </p>
            </div>
          )}
          <div className="flex justify-end">
            <button
              type="button"
              id="start-appointment-button"
              aria-disabled={joining || undefined}
              aria-busy={joining || undefined}
              onClick={handleStartAppointment}
              className={joinButtonClassName}
            >
              {joining ? 'Connecting…' : 'Start appointment'}
            </button>
          </div>
          {/*
            The status sits below the button so that it can appear and grow
            without displacing the control the patient just pressed, and both
            phases share one container so only the wording and the border
            colour change between them.
          */}
          <div
            className={
              joinPhase === 'idle'
                ? undefined
                : 'p-4 border-l-4 rounded-sm bg-callout-bg ' +
                  (joinPhase === 'still-waiting' ? 'border-l-nhs-yellow' : 'border-l-nhs-blue')
            }
          >
            {/*
              Persistent live region — must stay in the DOM (including while idle)
              so the screen reader registers it before Connecting… is written.
            */}
            <p
              role="status"
              aria-live="polite"
              aria-atomic="true"
              className={['m-0', joinPhase === 'idle' ? 'sr-only' : 'text-text'].join(' ')}
            >
              {joinStatusText(joinPhase)}
            </p>
          </div>
        </div>
      )}

      {allReady && !timeStatus.allowed && (
        <div
          role="status"
          className={[
            'flex flex-col gap-4 p-4 border-l-4 rounded-sm bg-callout-bg',
            timeStatus.reason === 'closed' ? 'border-l-nhs-red' : 'border-l-nhs-yellow',
          ].join(' ')}
        >
          <p className="text-text m-0">{timeStatus.message}</p>
          {timeStatus.reason === 'too_early' && (
            <p className="text-text m-0">
              You don&rsquo;t need to refresh this page &mdash; it will update automatically when
              it&rsquo;s time to join.
            </p>
          )}
          {timeStatus.reason === 'closed' && (
            <>
              <p className="text-text m-0 text-[15px]">
                Need help? Call <strong>01737 768 511</strong>.
              </p>
              <div>
                <button
                  type="button"
                  onClick={onReset}
                  className="bg-nhs-blue text-white font-bold px-6 py-3 text-base hover:bg-nhs-dark-blue rounded-sm transition-colors min-h-12"
                >
                  Re-enter your details
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {anyDenied && (
        <div
          role="alert"
          className="flex items-start gap-3 p-4 border-l-4 border-l-nhs-red bg-callout-bg rounded-sm"
        >
          <p className="text-text m-0">
            Some checks failed. Please allow access in your browser settings and
            <button
              type="button"
              onClick={() => {
                setStarted(false);
                setCamera('pending');
                setMicrophone('pending');
                setSpeaker('pending');
              }}
              className="text-nhs-blue font-bold underline ml-1 bg-transparent border-0 cursor-pointer p-0 min-h-12 inline-flex items-center"
            >
              try again
            </button>
            .
          </p>
        </div>
      )}
    </Card>
  );
}

// ── Form view ───────────────────────────────────────────────────

function FormView({
  data,
  setData,
  onContinue,
}: {
  data: AppointmentData;
  setData: (d: AppointmentData) => void;
  onContinue: () => void;
}) {
  const fnId = useId();
  const lnId = useId();
  const dpId = useId();
  const tmId = useId();

  const summaryRef = useRef<HTMLDivElement>(null);
  const fnRef = useRef<HTMLInputElement>(null);
  const lnRef = useRef<HTMLInputElement>(null);
  const dpRef = useRef<HTMLSelectElement>(null);
  const tmRef = useRef<HTMLInputElement>(null);

  const [errors, setErrors] = useState<FormErrors>({});
  const focusSummaryAfterSubmit = useRef(false);

  const errorCount = Object.keys(errors).length;

  useEffect(() => {
    if (focusSummaryAfterSubmit.current && errorCount > 0) {
      summaryRef.current?.focus();
      focusSummaryAfterSubmit.current = false;
    }
  }, [errors, errorCount]);

  function clearFieldError(field: keyof FormErrors) {
    setErrors(prev => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const nextErrors = validateForm(data);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      focusSummaryAfterSubmit.current = true;
      return;
    }
    onContinue();
  }

  const inputBase =
    'w-full border rounded-sm px-4 py-3 text-base text-text bg-white outline-none focus:border-nhs-blue min-h-[48px]';
  const inputOk = `${inputBase} border-border-input`;
  const inputErr = `${inputBase} border-2 border-nhs-red`;

  return (
    <Card>
      <ScreenHeading
        title="Enter your virtual appointment details"
        intro="Please enter the details from your appointment letter so we can verify your identity. Do not join more than 15 minutes before your scheduled time."
      />

      <form onSubmit={handleSubmit} noValidate className="mt-6">
        <p className="text-text-muted mb-6 m-0">All fields are required.</p>
        {errorCount > 0 && (
          <div
            ref={summaryRef}
            role="alert"
            aria-labelledby="error-summary-title"
            tabIndex={-1}
            className="mb-6 p-4 border-l-4 border-l-nhs-red bg-callout-bg rounded-sm outline-none"
          >
            <h3 id="error-summary-title" className="mt-0 mb-3 text-lg font-bold text-text">
              There is a problem
            </h3>
            <ul className="list-disc pl-5 m-0 space-y-2 text-text">
              {errors.firstName && (
                <li>
                  <button
                    type="button"
                    className="text-nhs-blue underline text-left bg-transparent border-0 cursor-pointer p-0 font-inherit"
                    onClick={() => fnRef.current?.focus()}
                  >
                    {errors.firstName}
                  </button>
                </li>
              )}
              {errors.lastName && (
                <li>
                  <button
                    type="button"
                    className="text-nhs-blue underline text-left bg-transparent border-0 cursor-pointer p-0 font-inherit"
                    onClick={() => lnRef.current?.focus()}
                  >
                    {errors.lastName}
                  </button>
                </li>
              )}
              {errors.department && (
                <li>
                  <button
                    type="button"
                    className="text-nhs-blue underline text-left bg-transparent border-0 cursor-pointer p-0 font-inherit"
                    onClick={() => dpRef.current?.focus()}
                  >
                    {errors.department}
                  </button>
                </li>
              )}
              {errors.time && (
                <li>
                  <button
                    type="button"
                    className="text-nhs-blue underline text-left bg-transparent border-0 cursor-pointer p-0 font-inherit"
                    onClick={() => tmRef.current?.focus()}
                  >
                    {errors.time}
                  </button>
                </li>
              )}
            </ul>
          </div>
        )}

        <div className="mb-5">
          <label htmlFor={fnId} className="block font-bold text-text mb-2">
            Patient first name
          </label>
          <input
            ref={fnRef}
            id={fnId}
            type="text"
            required
            autoComplete="given-name"
            value={data.firstName}
            onChange={e => {
              setData({ ...data, firstName: e.target.value });
              clearFieldError('firstName');
            }}
            className={errors.firstName ? inputErr : inputOk}
            aria-invalid={errors.firstName ? true : undefined}
            aria-describedby={errors.firstName ? `${fnId}-error` : undefined}
          />
          {errors.firstName && (
            <p id={`${fnId}-error`} className="text-nhs-red text-sm mt-2 m-0">
              {errors.firstName}
            </p>
          )}
        </div>

        <div className="mb-5">
          <label htmlFor={lnId} className="block font-bold text-text mb-2">
            Patient last name
          </label>
          <input
            ref={lnRef}
            id={lnId}
            type="text"
            required
            autoComplete="family-name"
            value={data.lastName}
            onChange={e => {
              setData({ ...data, lastName: e.target.value });
              clearFieldError('lastName');
            }}
            className={errors.lastName ? inputErr : inputOk}
            aria-invalid={errors.lastName ? true : undefined}
            aria-describedby={errors.lastName ? `${lnId}-error` : undefined}
          />
          {errors.lastName && (
            <p id={`${lnId}-error`} className="text-nhs-red text-sm mt-2 m-0">
              {errors.lastName}
            </p>
          )}
        </div>

        <div className="mb-5">
          <label htmlFor={dpId} className="block font-bold text-text mb-1">
            Department
          </label>
          <p id={`${dpId}-hint`} className="text-text-muted text-[15px] mb-2 m-0">
            As shown on your appointment letter
          </p>
          <select
            ref={dpRef}
            id={dpId}
            required
            aria-describedby={
              [`${dpId}-hint`, errors.department ? `${dpId}-error` : '']
                .filter(Boolean)
                .join(' ') || undefined
            }
            value={data.department}
            onChange={e => {
              setData({ ...data, department: e.target.value });
              clearFieldError('department');
            }}
            className={errors.department ? inputErr : inputOk}
            aria-invalid={errors.department ? true : undefined}
          >
            <option value="">Select a department</option>
            {DEPARTMENTS.map(dept => (
              <option key={dept} value={dept}>
                {dept}
              </option>
            ))}
          </select>
          {errors.department && (
            <p id={`${dpId}-error`} className="text-nhs-red text-sm mt-2 m-0">
              {errors.department}
            </p>
          )}
        </div>

        <div className="mb-6">
          <fieldset className="border-0 p-0 m-0">
            <legend className="font-bold text-text mb-2 p-0">Appointment time</legend>
            <p className="text-text m-0 mb-2" aria-live="off">
              {formatDateOnly(new Date())}
            </p>
            <label htmlFor={tmId} className="sr-only">
              Time of appointment
            </label>
            <input
              ref={tmRef}
              id={tmId}
              type="time"
              required
              value={data.time}
              onChange={e => {
                setData({ ...data, time: e.target.value });
                clearFieldError('time');
              }}
              className={errors.time ? inputErr : inputOk}
              aria-invalid={errors.time ? true : undefined}
              aria-describedby={errors.time ? `${tmId}-error` : undefined}
            />
            {errors.time && (
              <p id={`${tmId}-error`} className="text-nhs-red text-sm mt-2 m-0">
                {errors.time}
              </p>
            )}
          </fieldset>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            className="bg-nhs-blue text-white font-bold px-8 py-3 text-base hover:bg-nhs-dark-blue rounded-sm transition-colors min-h-12"
          >
            Submit
          </button>
        </div>
      </form>
    </Card>
  );
}

// ── Container ───────────────────────────────────────────────────

export default function AppointmentForm({
  searchParams,
}: {
  searchParams: Record<string, string>;
}) {
  const initial = parseParams(searchParams);

  const [step, setStep] = useState<Step>(initial.hasQS ? 'confirm' : 'form');
  const [data, setData] = useState<AppointmentData>(initial.data);
  const [avcheckComplete, setAvcheckComplete] = useState(false);
  const stepContainerRef = useRef<HTMLDivElement>(null);
  const prevStepRef = useRef<Step | null>(null);

  useEffect(() => {
    if (initial.hasQS) {
      setGlobalVars(initial.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const prev = prevStepRef.current;
    prevStepRef.current = step;
    // Guard: bail on initial mount and on StrictMode's double-invoke
    // (both produce prev === null or prev === step).
    if (prev === null || prev === step) return;
    setAvcheckComplete(false);
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduced ? 'instant' : 'smooth' });
    const h2 = stepContainerRef.current?.querySelector<HTMLHeadingElement>('h2');
    if (h2) {
      h2.setAttribute('tabindex', '-1');
      h2.focus({ preventScroll: true });
    }
  }, [step]);

  function handleContinueFromForm() {
    setGlobalVars(data);
    setStep('confirm');
  }

  function handleStepNavigate(target: Step) {
    const cur = stepIndex(step);
    const t = stepIndex(target);
    if (t < cur) {
      setStep(target);
    }
  }

  const appointmentIso = data.time ? todayAtTime(data.time) : '';

  return (
    <div ref={stepContainerRef}>
      <Stepper current={step} currentComplete={avcheckComplete} onNavigate={handleStepNavigate} />
      {step === 'form' && (
        <FormView data={data} setData={setData} onContinue={handleContinueFromForm} />
      )}
      {step === 'confirm' && (
        <ConfirmView
          data={data}
          onBack={() => setStep('form')}
          onConfirm={() => setStep('avcheck')}
        />
      )}
      {step === 'avcheck' && (
        <AVCheckView
          patientName={`${data.firstName} ${data.lastName}`}
          appointmentDateTime={appointmentIso}
          onReset={() => setStep('form')}
          onAllReady={() => setAvcheckComplete(true)}
        />
      )}
    </div>
  );
}
